import React, { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/api/apiClient";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tag, Search, Trash2, Filter, ListChecks, CheckCheck, X, Check } from "lucide-react";
import { AnimatePresence, m } from "framer-motion";
import { toast } from "sonner";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import MatchCard from "../components/matches/MatchCard";
import FilterPanel from "../components/swipe/FilterPanel";
import { normalizeCategory } from "@/utils/categoryUtils";
import { normalizeColor, getColorName } from "@/utils/colorUtils";
import { useCategories } from "@/hooks/useCategories";
import { fuzzyMatchProduct } from "@/utils/fuzzySearch";
import SkeletonGrid from "../components/ui/SkeletonGrid";

export default function CategoriesPage() {
    const location = useLocation();
    // console.log("Rendering CategoriesPage"); // Debug log
    const urlParams = new URLSearchParams(location.search);
    const categoryName = urlParams.get('name');
    const [searchParams, setSearchParams] = useSearchParams();

    // Use the categories hook
    const { categories: userCategories, removeCategory, isLoading: loadingCategories } = useCategories();

    // Selection mode state
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState(new Set());
    const [showBatchDeleteDialog, setShowBatchDeleteDialog] = useState(false);
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);

    // Initialize state from URL params
    const [searchQuery, setSearchQuery] = useState(() => searchParams.get("q") || "");
    const [productToDelete, setProductToDelete] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState(() => ({
        minPrice: Number(searchParams.get("min_price")) || 0,
        maxPrice: Number(searchParams.get("max_price")) || 10000,
        categories: [], // Not used in this page usually?
        subcategories: searchParams.get("subcategories") ? searchParams.get("subcategories").split(",") : [],
        colors: searchParams.get("colors") ? searchParams.get("colors").split(",") : [],
        brands: searchParams.get("brands") ? searchParams.get("brands").split(",") : [],
        sizes: searchParams.get("sizes") ? searchParams.get("sizes").split(",") : [],
        inStockOnly: searchParams.get("in_stock") === "true",
    }));

    // Sync State -> URL
    useEffect(() => {
        const params = {};
        if (categoryName) params.name = categoryName; // Preserves category
        if (searchQuery) params.q = searchQuery;
        if (filters.minPrice > 0) params.min_price = filters.minPrice.toString();
        if (filters.maxPrice < 10000) params.max_price = filters.maxPrice.toString();
        if (filters.subcategories.length > 0) params.subcategories = filters.subcategories.join(",");
        if (filters.colors.length > 0) params.colors = filters.colors.join(",");
        if (filters.brands.length > 0) params.brands = filters.brands.join(",");
        if (filters.sizes.length > 0) params.sizes = filters.sizes.join(",");
        if (filters.inStockOnly) params.in_stock = "true";

        setSearchParams(params, { replace: true });
    }, [searchQuery, filters, categoryName, setSearchParams]);

    // Back/Forward Sync
    useEffect(() => {
        const currentParams = Object.fromEntries(searchParams.entries());

        // Only update if not navigating to different category
        if (currentParams.name === categoryName) {
            setSearchQuery(currentParams.q || "");
            setFilters(prev => ({
                ...prev,
                minPrice: Number(currentParams.min_price) || 0,
                maxPrice: Number(currentParams.max_price) || 10000,
                subcategories: currentParams.subcategories ? currentParams.subcategories.split(",") : [],
                colors: currentParams.colors ? currentParams.colors.split(",") : [],
                brands: currentParams.brands ? currentParams.brands.split(",") : [],
                sizes: currentParams.sizes ? currentParams.sizes.split(",") : [],
                inStockOnly: currentParams.in_stock === "true",
            }));
        }
    }, [searchParams, categoryName]);

    const categoryProductsIds = userCategories
        .filter(c => c.category_name === categoryName)
        .map(c => c.product_id);

    // Fetch ONLY the products in this category (bypass default limit of 50)
    // We use a new query key dependent on the category IDs
    const { data: products = [], isLoading: loadingProducts } = useQuery({
        queryKey: ['products', 'by_ids', categoryProductsIds.sort().join(',')], // Stable key
        queryFn: () => {
            if (categoryProductsIds.length === 0) return Promise.resolve([]);
            // Pass IDs as filter and set a high limit (e.g. 1000) to get all collection items
            // Pass IDs as filter and set a high limit (e.g. 1000) to get all collection items
            return apiClient.entities.Product.list('-last_scraped_at', {
                ids: categoryProductsIds.join(','),
                limit: 1000,
                exclude_seen: 'false',
                include_deleted: 'false'
            });
        },
        enabled: categoryProductsIds.length > 0
    });

    // Determine loading state (category loading OR product loading)
    const isLoading = loadingCategories || (categoryProductsIds.length > 0 && loadingProducts);

    // console.log('[DEBUG] Category:', categoryName);
    // console.log('[DEBUG] IDs expected:', categoryProductsIds);
    // console.log('[DEBUG] Products fetched from API:', products.length);

    // Filter logic: Ensure we only show products that match our category IDs
    // This acts as a safety net if the API disregards the ID filter
    const productsInCurrentCategory = products.filter(p => {
        const match = categoryProductsIds.some(id => String(id) === String(p.id));
        if (!match) {
            // console.warn('[DEBUG] Mismatch or API returned extra product:', p.id, p.name);
        }
        return match;
    });



    // console.log('[DEBUG] Products matched locally:', productsInCurrentCategory.length);


    // Fetch dynamic category rules (Same logic as Recherche.js)
    const { data: rulesData } = useQuery({
        queryKey: ['categoryRules'],
        queryFn: () => apiClient.entities.UserCategory.getSystemRules().catch(() => []),
        staleTime: 1000 * 60 * 5, // 5 min
    });
    const rules = rulesData || [];

    // Dynamic Category Matcher (Same logic as Recherche.js)
    const matchCategory = React.useCallback((rawCat) => {
        if (!rawCat) return null;
        const lower = rawCat.trim().toLowerCase();
        for (const rule of rules) {
            for (const keyword of rule.keywords) {
                if (lower.includes(keyword.toLowerCase())) {
                    return rule.name;
                }
            }
        }
        return normalizeCategory(rawCat);
    }, [rules]);

    const filteredProducts = productsInCurrentCategory.filter(product => {
        // Search filter - fuzzy matching
        const matchesSearch = fuzzyMatchProduct(product, searchQuery);

        if (!matchesSearch) return false;

        // Apply advanced filters
        if (product.price < filters.minPrice || product.price > filters.maxPrice) return false;

        // Filter by subcategories
        if (filters.subcategories.length > 0) {
            const rawCat = product.subcategory || product.style;
            const normalized = rules.length > 0 ? matchCategory(rawCat) : normalizeCategory(rawCat);
            if (!filters.subcategories.includes(normalized)) return false;
        }

        // Filter by colors
        if (filters.colors && filters.colors.length > 0) {
            if (!product.colors || product.colors.length === 0) return false;
            const productColors = product.colors.map(c => normalizeColor(getColorName(c)));
            if (!productColors.some(c => filters.colors.includes(c))) return false;
        }

        if (filters.brands && filters.brands.length > 0 && !filters.brands.includes(product.brand)) return false;
        if (filters.sizes.length > 0 && !product.sizes?.some(s => filters.sizes.includes(s))) return false;
        if (filters.inStockOnly) {
            const hasAvailableSize = product.sizes && product.sizes.length > 0 && product.sizes.some(s => typeof s === 'string' && !s.includes('(Out of Stock)'));
            if (!hasAvailableSize) return false;
        }

        return true;
    });



    const handleToggleSelect = useCallback((product) => {
        const pId = String(product.id);
        setSelectedProductIds(prev => {
            const next = new Set(prev);
            if (next.has(pId)) {
                next.delete(pId);
            } else {
                next.add(pId);
            }
            return next;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        if (selectedProductIds.size === filteredProducts.length) {
            setSelectedProductIds(new Set());
        } else {
            setSelectedProductIds(new Set(filteredProducts.map(p => String(p.id))));
        }
    }, [filteredProducts, selectedProductIds.size]);

    const handleBatchDelete = useCallback(async () => {
        if (selectedProductIds.size === 0) return;
        setIsBatchDeleting(true);
        try {
            const categoriesToDelete = userCategories.filter(
                c => selectedProductIds.has(String(c.product_id)) && c.category_name === categoryName
            );
            await Promise.all(categoriesToDelete.map(c => removeCategory(c.id)));
            toast.success(`${selectedProductIds.size} vêtement${selectedProductIds.size > 1 ? 's' : ''} retiré${selectedProductIds.size > 1 ? 's' : ''} de la collection`);
            setSelectedProductIds(new Set());
            setIsSelectionMode(false);
            setShowBatchDeleteDialog(false);
        } catch (err) {
            console.error("Batch delete error:", err);
            toast.error("Erreur lors de la suppression des vêtements");
        } finally {
            setIsBatchDeleting(false);
        }
    }, [selectedProductIds, userCategories, categoryName, removeCategory]);

    const handleCancelSelection = useCallback(() => {
        setIsSelectionMode(false);
        setSelectedProductIds(new Set());
    }, []);

    const handleDeselectAll = useCallback(() => {
        setSelectedProductIds(new Set());
    }, []);

    if (isLoading) {
        return (
            <div className="min-h-screen p-4 md:p-8" aria-busy="true" aria-label="Chargement de la catégorie">
                <div className="max-w-7xl mx-auto">
                    {/* Header skeleton with category icon */}
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="skeleton-bone skeleton-rounded-xl skeleton-stagger-1" style={{ width: '3rem', height: '3rem' }} />
                            <div>
                                <div className="skeleton-bone skeleton-stagger-1" style={{ height: '1.875rem', width: '10rem' }} />
                                <div className="skeleton-bone skeleton-stagger-2 mt-1" style={{ height: '0.875rem', width: '6rem' }} />
                            </div>
                        </div>
                    </div>
                    {/* Search + Filter skeleton */}
                    <div className="mb-6 flex gap-3 items-center">
                        <div className="skeleton-bone skeleton-rounded-lg skeleton-searchbar skeleton-stagger-2" />
                        <div className="skeleton-bone skeleton-rounded-lg skeleton-filter-btn skeleton-stagger-3" />
                    </div>
                    {/* Grid skeleton */}
                    <SkeletonGrid count={10} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 md:p-8 pb-28">
            <div className="max-w-7xl mx-auto">
                {/* Header with Title & Selection Controls */}
                <div className="mb-6">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-600 to-amber-700 flex items-center justify-center shadow-lg shadow-amber-600/20">
                                <Tag className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{categoryName}</h1>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">
                                    {filteredProducts.length} produit{filteredProducts.length !== 1 ? 's' : ''} dans cette collection
                                </p>
                            </div>
                        </div>

                        {/* Top Selection Activation Button */}
                        {filteredProducts.length > 0 && !isSelectionMode && (
                            <Button
                                variant="outline"
                                onClick={() => setIsSelectionMode(true)}
                                className="gap-2 h-10 border-gray-300 dark:border-gray-700 hover:border-amber-500 hover:text-amber-600 transition-colors shadow-xs"
                            >
                                <ListChecks className="w-4 h-4" />
                                Sélectionner
                            </Button>
                        )}
                    </div>

                    {/* Sticky Active Selection Toolbar that follows the user */}
                    <AnimatePresence>
                        {isSelectionMode && (
                            <m.div
                                initial={{ opacity: 0, y: -10, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.98 }}
                                className="sticky top-3 z-30 mb-4 bg-amber-500/10 dark:bg-amber-950/40 backdrop-blur-xl border-2 border-amber-500/40 dark:border-amber-600/50 shadow-lg rounded-2xl p-3 flex flex-wrap items-center justify-between gap-3"
                            >
                                <div className="flex items-center gap-2.5 pl-1">
                                    <span className="flex h-3 w-3 rounded-full bg-amber-500 animate-pulse" />
                                    <div>
                                        <span className="text-sm font-bold text-amber-950 dark:text-amber-200">
                                            Mode sélection actif
                                        </span>
                                        <span className="text-xs text-amber-800/80 dark:text-amber-300/80 ml-2">
                                            ({selectedProductIds.size} sur {filteredProducts.length} sélectionné{selectedProductIds.size > 1 ? 's' : ''})
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleSelectAll}
                                        className="text-xs h-8 gap-1.5 bg-white/80 dark:bg-zinc-800/80 border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 hover:bg-amber-100/70"
                                    >
                                        <CheckCheck className="w-3.5 h-3.5" />
                                        {selectedProductIds.size === filteredProducts.length ? "Tout désélectionner" : "Tout sélectionner"}
                                    </Button>
                                    <Button
                                        variant="default"
                                        size="sm"
                                        onClick={handleCancelSelection}
                                        className="text-xs h-8 gap-1 bg-gray-900 dark:bg-zinc-700 hover:bg-gray-800 text-white shadow-xs"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                        Annuler
                                    </Button>
                                </div>
                            </m.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Search and Filter */}
                <div className="mb-6 flex gap-3 items-center">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                            placeholder="Rechercher dans cette catégorie..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 h-10"
                        />
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => setShowFilters(true)}
                        className="gap-2 h-10"
                    >
                        <Filter className="w-4 h-4" />
                        Filtres
                    </Button>
                </div>

                {/* Products Grid */}
                {filteredProducts.length === 0 ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center max-w-md">
                            <div className="w-24 h-24 bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-950/30 dark:to-yellow-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Tag className="w-12 h-12 text-amber-600 dark:text-amber-400" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                                {searchQuery || filters.minPrice > 0 || filters.maxPrice < 500 || filters.subcategories.length > 0 ? "Aucun résultat" : "Catégorie vide"}
                            </h2>
                            <p className="text-gray-600">
                                {searchQuery || filters.minPrice > 0 || filters.maxPrice < 500 || filters.subcategories.length > 0
                                    ? "Essayez d'autres mots-clés ou ajustez vos filtres"
                                    : "Ajoutez des produits à cette catégorie depuis les pages Swipe, Matches ou Historique"}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-6">
                        <AnimatePresence>
                            {filteredProducts.map(product => (
                                <MatchCard
                                    key={product.id}
                                    product={product}
                                    onDelete={(id) => setProductToDelete(id)}
                                    source={`category:${categoryName}`}
                                    isSelectable={isSelectionMode}
                                    isSelected={selectedProductIds.has(String(product.id))}
                                    onToggleSelect={() => handleToggleSelect(product)}
                                />
                            ))}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {/* Barre flottante d'actions pour la sélection multiple qui suit l'utilisateur - Centrée sur la zone de page (excluant la sidebar) */}
            <AnimatePresence>
                {isSelectionMode && (
                    <div className="fixed bottom-6 inset-x-0 md:left-64 z-50 flex justify-center pointer-events-none px-4">
                        <m.div
                            initial={{ opacity: 0, y: 30, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 30, scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 350, damping: 25 }}
                            className="pointer-events-auto max-w-lg w-full bg-gray-950/95 dark:bg-zinc-900/95 text-white backdrop-blur-xl border border-white/15 dark:border-zinc-800 shadow-2xl rounded-2xl p-3 sm:px-4 sm:py-3 flex items-center justify-between gap-3"
                        >
                            <div className="flex items-center gap-2 pl-1 min-w-0">
                                <span className="flex h-2.5 w-2.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
                                <span className="text-sm font-semibold truncate">
                                    {selectedProductIds.size > 0
                                        ? `${selectedProductIds.size} vêtement${selectedProductIds.size > 1 ? 's' : ''} sélectionné${selectedProductIds.size > 1 ? 's' : ''}`
                                        : "Touchez un vêtement pour le sélectionner"
                                    }
                                </span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {selectedProductIds.size > 0 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleDeselectAll}
                                        className="text-xs text-gray-300 hover:text-white hover:bg-white/10 h-8 px-2.5"
                                    >
                                        Désélectionner tout
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleCancelSelection}
                                    className="text-xs text-gray-300 hover:text-white hover:bg-white/10 h-8 px-2.5"
                                >
                                    Annuler
                                </Button>
                                {selectedProductIds.size > 0 && (
                                    <Button
                                        size="sm"
                                        onClick={() => setShowBatchDeleteDialog(true)}
                                        className="bg-red-500 hover:bg-red-600 text-white font-medium text-xs h-8 px-3 gap-1.5 shadow-lg shadow-red-500/25 transition-all active:scale-95"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        Supprimer ({selectedProductIds.size})
                                    </Button>
                                )}
                            </div>
                        </m.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Single Delete Confirmation Dialog */}
            <AlertDialog open={!!productToDelete} onOpenChange={() => setProductToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Retirer de la catégorie ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Ce produit sera retiré de cette catégorie.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                const categoriesToDelete = userCategories.filter(
                                    c => String(c.product_id) === String(productToDelete) && c.category_name === categoryName
                                );
                                categoriesToDelete.forEach(c => removeCategory(c.id));
                                setProductToDelete(null);
                            }}
                            className="bg-red-500 hover:bg-red-600"
                        >
                            Retirer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Batch Delete Confirmation Dialog */}
            <AlertDialog open={showBatchDeleteDialog} onOpenChange={setShowBatchDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Retirer {selectedProductIds.size} vêtement{selectedProductIds.size > 1 ? 's' : ''} de la catégorie ?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Ces {selectedProductIds.size} article{selectedProductIds.size > 1 ? 's' : ''} seront définitivement retiré{selectedProductIds.size > 1 ? 's' : ''} de votre collection "{categoryName}".
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isBatchDeleting}>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleBatchDelete}
                            disabled={isBatchDeleting}
                            className="bg-red-500 hover:bg-red-600 gap-1.5"
                        >
                            <Trash2 className="w-4 h-4" />
                            {isBatchDeleting ? "Suppression en cours..." : `Retirer (${selectedProductIds.size})`}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Filter Panel Dialog */}
            <Dialog open={showFilters} onOpenChange={setShowFilters}>
                <DialogContent className="sm:max-w-md p-0">
                    <FilterPanel
                        filters={filters}
                        setFilters={setFilters}
                        onClose={() => setShowFilters(false)}
                        products={productsInCurrentCategory}
                        availableOptions={rules.length > 0 ? { styles: rules.map(r => r.name) } : undefined}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}